import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Heading,
  Link,
  Stack,
  Text,
  UseDisclosureReturn,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExpiryReason, RateImportState } from '@pelicargo/types';
import { Show, useSuccess } from '@pelicargo/ui';
import { expiryReasonOptions, prettyDate } from '@pelicargo/utils';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useAirportTimeZone } from '../../hooks/airport/useAirportTimeZone';
import { useEarliestExpiringRate } from '../../hooks/import/useEarliestExpiringRate';
import { BaseDrawer } from '../BaseDrawer';
import { LabeledText } from '../LabeledText';
import { SimpleDatePicker } from '../SimpleDatePicker';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';

const schema = z.object({
  expiry_reason: z.nativeEnum(ExpiryReason, {
    required_error: 'Expiry reason is required',
    invalid_type_error: 'Invalid expiry reason',
  }),
  expires_at: z.date().default(() => new Date()),
  expiry_comment: z.string().nullable(),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  isOpen: UseDisclosureReturn['isOpen'];
  onClose: UseDisclosureReturn['onClose'];
  rateImportToExpire: any;
};

export const ExpiryDrawer = ({ isOpen, onClose, rateImportToExpire }: Props) => {
  const { handleSuccess } = useSuccess();
  const toast = useToast();
  const methods = useForm({
    resolver: zodResolver(schema),
  });
  const reason = methods.watch('expiry_reason');
  const [earliestExpiredRate] = useEarliestExpiringRate(rateImportToExpire?.id as number);
  const { data: originAirport } = useAirportTimeZone(earliestExpiredRate?.origin_airport_id);

  const { mutateAsync: expireRate, isLoading: isExpiring } = trpc.expireImport.useMutation();

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleExpirySubmit = async (data: any) => {
    try {
      if (rateImportToExpire?.state === RateImportState.EXPIRED) {
        throw new Error('Import already expired');
      }
      if (!rateImportToExpire?.id) return;

      await expireRate({ ...data, import_id: rateImportToExpire?.id } as any); // TODO: resolve typing
      handleSuccess('Successfully expired import');
      onClose();
    } catch (error: any) {
      toast({
        title: 'Failed to expire import',
        description: error.message,
        status: 'error',
      });
    }
  };

  const handleSubmit = methods.handleSubmit(handleExpirySubmit);

  const isInvalid = useMemo(() => reason === ExpiryReason.INVALID_ENTRY, [reason]);

  return (
    <BaseDrawer onClose={handleClose} isOpen={isOpen}>
      <DrawerHeader>
        <Heading variant="h4">Expire Import</Heading>
      </DrawerHeader>
      <DrawerBody>
        <SimpleForm {...methods} submitOnBlur={false}>
          <form onSubmit={handleSubmit}>
            <Alert status="warning" mb="5">
              <AlertIcon />
              <Stack spacing={0}>
                <AlertTitle>This is irreversible.</AlertTitle>
                <AlertDescription fontSize="sm">
                  Proceed with caution and confirm the import you are expiring.
                </AlertDescription>
              </Stack>
            </Alert>
            <Stack spacing={6}>
              <LabeledText label="Name" value={rateImportToExpire?.name} />
              <LabeledText
                label="Earliest Expiry"
                value={prettyDate(earliestExpiredRate?.expires_at as any, {
                  timeZone: originAirport?.time_zone,
                })}
              />
              <LabeledText label="Provider" value={rateImportToExpire?.provider?.name} />
              <Divider />
              <SimpleSelect
                name="expiry_reason"
                label="Expiry Reason"
                variant="filled"
                options={expiryReasonOptions}
                helper="This is used for data analysis purposes."
                isRequired
              />
              <SimpleDatePicker<FormValues>
                name="expires_at"
                label="Expiry Date"
                variant="filled"
                helper={
                  isInvalid
                    ? 'Selecting Invalid Entry will automatically set the expiration date to immediate.'
                    : undefined
                }
                isDisabled={isInvalid}
                showTime={false}
                defaultValue={earliestExpiredRate?.expires_at}
                isRequired
                matchWidth
                showClearButton
              />
              <SimpleInput name="expiry_comment" label="Comment" helper="Optional, but recommended." variant="filled" />
            </Stack>
          </form>
        </SimpleForm>
      </DrawerBody>
      <DrawerFooter>
        <Stack
          direction="row"
          justify={rateImportToExpire?.id ? 'space-between' : 'right'}
          align="center"
          w="full"
          spacing="4"
        >
          <Show if={!!rateImportToExpire?.id}>
            <Text>ID: {rateImportToExpire?.id}</Text>
          </Show>

          <Stack direction="row" align="center" justify="flex-end" spacing="4">
            <Button
              as={Link}
              href={rateImportToExpire?.source || ''}
              isExternal
              variant="link"
              size="sm"
              target="_blank"
              rightIcon={<HiArrowTopRightOnSquare />}
            >
              Open Link to Sheet
            </Button>
            <Button colorScheme="red" isLoading={isExpiring} onClick={handleSubmit}>
              Expire
            </Button>
          </Stack>
        </Stack>
      </DrawerFooter>
    </BaseDrawer>
  );
};
